import React from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDeleteConfirmState } from '@/components/hooks';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Edit2, Trash2, Archive, ArchiveRestore, ThumbsUp, Award, AlertTriangle, Clock } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export default function PolicyCard({ policy, officers, issues, capabilities, onEdit, isSeniorOfficer, onApprove, onInstitutionalize, onArchive, onUnarchive }) {
  const queryClient = useQueryClient();
  const deleteConfirm = useDeleteConfirmState({ confirmTimeout: 3000 });

  const deletePolicyMutation = useMutation({
    mutationFn: (id) => base44.entities.Policy.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const retirePolicyMutation = useMutation({
    mutationFn: (id) =>
    base44.entities.Policy.update(id, {
      status: 'retired',
      retired_at: new Date().toISOString()
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['policies'] })
  });

  const designer = officers.find((o) => o.user_email === policy.designed_by);
  const linkedIssues = issues.filter((i) => policy.linked_issue_ids?.includes(i.id));
  const requestedCapability = capabilities.find((c) => c.id === policy.capability_request_id);
  const linkedCapabilities = (policy.linked_capability_ids || []).map(id => capabilities.find(c => c.id === id)).filter(Boolean);

  const isBridging = policy.interim_for_capability_development;
  const now = new Date();
  const policyAgeDays = policy.designed_at ? differenceInDays(now, new Date(policy.designed_at)) : null;
  const reviewDate = policy.review_date ? new Date(policy.review_date) : null;
  const reviewDaysLeft = reviewDate ? differenceInDays(reviewDate, now) : null;
  const isOverdue = reviewDate && reviewDaysLeft < 0;

  const statusColors = {
    draft: 'bg-slate-500/20 text-slate-400',
    active: 'bg-emerald-500/20 text-emerald-400',
    under_review: 'bg-amber-500/20 text-amber-400',
    retired: 'bg-red-500/20 text-red-400',
    approved: 'bg-emerald-500/20 text-emerald-400',
    institutionalized: 'bg-indigo-500/20 text-indigo-400',
    pending_approval: 'bg-amber-500/20 text-amber-400'
  };

  const enforceabilityColors = {
    easy: 'bg-emerald-500/20 text-emerald-400',
    moderate: 'bg-amber-500/20 text-amber-400',
    difficult: 'bg-orange-500/20 text-orange-400',
    unenforceable: 'bg-red-500/20 text-red-400'
  };

  return (
    <Card className={`glass-panel border-slate-800 ${isBridging ? 'border-l-4 border-l-amber-500/60' : ''}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-5 h-5 text-amber-400" />
              <CardTitle className="text-lg">{policy.title}</CardTitle>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className={statusColors[policy.status] || statusColors.draft}>
                {policy.status?.replace('_', ' ')}
              </Badge>
              {isBridging && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  BRIDGING POLICY
                </Badge>
              )}
              {policy.enforceability_score && (
                <Badge className={enforceabilityColors[policy.enforceability_score] || ''}>
                  {policy.enforceability_score}
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(policy)}>
              <Edit2 className="w-4 h-4" />
            </Button>
            {policy.status === 'retired' ? (
              onUnarchive && (
                <Button size="icon" variant="ghost" className="h-8 w-8 text-amber-400 hover:text-amber-300" onClick={() => onUnarchive(policy.id)} title="Unretire">
                  <ArchiveRestore className="w-4 h-4" />
                </Button>
              )
            ) : (
              onArchive && (
                <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-amber-400" onClick={() => onArchive(policy.id)} title="Retire">
                  <Archive className="w-4 h-4" />
                </Button>
              )
            )}
            <div className="relative">
              <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-red-400" onClick={() => deleteConfirm.checkAndConfirm(policy.id, () => deletePolicyMutation.mutate(policy.id))}>
                <Trash2 className="w-4 h-4" />
              </Button>
              {deleteConfirm.isPendingConfirm(policy.id) && (
                <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                  Click again to confirm
                </span>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs text-slate-500 font-semibold mb-1">Policy Statement:</p>
          <p className="text-slate-700 text-sm">{policy.statement}</p>
        </div>

        {policy.restrictions && (
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-1">Restrictions:</p>
            <p className="text-sm text-red-400">{policy.restrictions}</p>
          </div>
        )}

        {policy.rationale && (
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-1">Rationale:</p>
            <p className="text-sm text-slate-400">{policy.rationale}</p>
          </div>
        )}

        {/* Bridging Policy Info */}
        {isBridging && (
          <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-950/20 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Clock className="w-4 h-4 text-amber-400" />
              {reviewDate ? (
                isOverdue ? (
                  <span className="text-sm font-semibold text-red-400">
                    OVERDUE — review was {Math.abs(reviewDaysLeft)} days ago
                  </span>
                ) : (
                  <span className="text-sm text-amber-300">
                    Review in {reviewDaysLeft} days ({format(reviewDate, 'MMM d, yyyy')})
                  </span>
                )
              ) : (
                <Badge className="bg-red-500/20 text-red-400">No review date set!</Badge>
              )}
            </div>
            {policyAgeDays !== null && policyAgeDays > 90 && (
              <div className="flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle className="w-3 h-3" />
                Bridging policy exceeding 90-day target ({policyAgeDays} days old)
              </div>
            )}
            {policy.sunset_trigger && (
              <div className="text-xs text-slate-400">
                <span className="text-amber-400 font-medium">Sunset trigger:</span> {policy.sunset_trigger}
              </div>
            )}
            {linkedCapabilities.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-amber-400 font-medium">Bridging for:</p>
                <div className="flex flex-wrap gap-1">
                  {linkedCapabilities.map(cap => (
                    <Badge key={cap.id} variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-xs">
                      {cap.name} ({cap.status?.replace('_', ' ')})
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Policy Age */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-slate-500">Designed by:</span>
            <span className="text-slate-600 ml-2">{designer?.callsign || policy.designed_by}</span>
          </div>
          {policy.designed_at && (
            <div>
              <span className="text-slate-500">Created:</span>
              <span className="text-slate-600 ml-2">
                {format(new Date(policy.designed_at), 'MMM d, yyyy')}
                {policyAgeDays !== null && (
                  <span className={`ml-1 ${policyAgeDays > 90 && isBridging ? 'text-red-400 font-semibold' : 'text-slate-500'}`}>
                    ({policyAgeDays}d)
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {linkedIssues.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 font-semibold mb-2">Addresses Issues:</p>
            <div className="flex flex-wrap gap-1">
              {linkedIssues.map((issue) => (
                <Badge key={issue.id} variant="outline" className="bg-red-500/10 text-red-400 border-red-500/30 text-xs">
                  {issue.title}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {requestedCapability && (
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <p className="text-xs text-emerald-400 font-semibold mb-1">Capability Requested to Replace Policy</p>
            <p className="text-xs text-slate-300">{requestedCapability.name}</p>
          </div>
        )}

        {isSeniorOfficer && policy.status === 'draft' && onApprove && (
          <Button data-testid="issues-approve-btn" size="sm" className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => onApprove(policy.id)}>
            <ThumbsUp className="w-3 h-3 mr-1" /> Approve
          </Button>
        )}

        {isSeniorOfficer && policy.status === 'approved' && onInstitutionalize && (
          <Button data-testid="issues-institutionalize-btn" size="sm" className="w-full h-8 text-xs bg-indigo-600 hover:bg-indigo-700" onClick={() => onInstitutionalize(policy.id)}>
            <Award className="w-3 h-3 mr-1" /> Institutionalize
          </Button>
        )}
      </CardContent>
    </Card>
  );
}